import { Container, Graphics } from 'pixi.js';
import { OFFICE_ZONES, type WorldZone } from '@duck/contracts';
import { isReducedMotion } from './reduced-motion';

/**
 * Procedural rendering of the ROOM-LOCAL disco (ADR-0031 "Jukebox"). Unlike the old random world event
 * (which washed the WHOLE floor), disco is now PLAYER-DRIVEN and per-room: the authoritative room state
 * carries a set of zone ids whose disco is on (`discoZones`, toggled by the 🎵 Jukebox), and THIS layer
 * lights exactly — and only — those rooms' floors. A reason to gather + dance, never a task or a metric
 * (Constitution Art. 1). Per ADR-0010 the visuals are CODE-AUTHORED Pixi primitives — no art assets.
 *
 * PERFORMANCE is a first-class gate (like {@link WorldEventLayer}):
 *   • a room's tile grid is built ONCE when its disco turns on and reused while it runs — {@link update}
 *     only animates tint/alpha, never `new`s per frame;
 *   • only LIT rooms hold any nodes — an unlit office is empty (no disco → zero cost);
 *   • each tile is a single tinted Graphics (one draw call), a coarse grid, not a particle storm.
 *
 * ACCESSIBILITY is the other gate. This layer is DECORATIVE and lives BELOW the ducks/labels/badges, so
 * the wash tints the FLOOR only — the foreground stays fully legible. Reduced motion → a correct STATIC
 * frame (a steady tinted glow, no colour-cycling). The dual-coded read-out lives in the DOM HUD.
 */

/** Target on-screen size (world units) of one disco light cell — the room's grid is sized to fit. */
const TILE_SIZE = 90;

/** How fast the disco hue cycles (full rainbow loop, seconds). Gentle, not a strobe (a11y/photosensitivity). */
const DISCO_CYCLE_SEC = 6;

/** How fast a room's wash eases in/out so a toggle washes in/out rather than popping. */
const FADE_PER_SEC = 2.4;

/** A gentle, fixed disco hue: the reduced-motion steady glow + the initial tile tint (a soft magenta). */
const STATIC_DISCO_TINT = 0xc77dff;

/** One soft, color-cycling cell of a room's disco floor wash (a tinted Graphics we animate). */
interface DiscoTile {
  gfx: Graphics;
  /** Phase offset so cells cycle out of sync → a lively, moving rainbow rather than one flat colour. */
  phase: number;
}

/** The live disco wash for ONE room: a pooled tile grid eased in/out via a single container alpha. */
interface ZoneDisco {
  container: Container;
  tiles: DiscoTile[];
  /** True while the room's disco is ON; once false we fade out, then destroy when invisible. */
  active: boolean;
  /** Eased presence (0 = gone, 1 = full) so a toggle fades rather than pops. */
  amount: number;
}

export class DiscoZoneLayer {
  /**
   * Added by the engine BELOW the ducks (alongside the world-event layer) so the wash tints/sits on the
   * floor and never occludes the foreground. World-space: tiles are drawn at absolute room coordinates.
   */
  readonly root = new Container();

  /** Live washes keyed by zone id. A room appears here only while lit (or fading out). */
  private readonly zones = new Map<string, ZoneDisco>();

  /** Monotonic seconds for the hue phase — advanced from dt, never `performance.now()` (no alloc). */
  private clock = 0;
  private firstFrame = true;

  /** The resolved level's zones (Phase 3) the disco grid is clipped to. Defaults to the office; the engine
   *  swaps it via {@link setZones} when it resolves the level (so castle jukeboxes light castle rooms). */
  private zoneGeometry: readonly WorldZone[] = OFFICE_ZONES;

  /** Point the disco at a level's zones (Phase 3). Called by the engine on init + on a level swap. */
  setZones(zones: readonly WorldZone[]): void {
    this.zoneGeometry = zones;
  }

  /**
   * Reconcile the lit rooms to the authoritative set (idempotent). Newly-lit rooms build their grid and
   * fade in; rooms no longer in the set fade out (and are destroyed once invisible). Cheap: called only
   * when the synced `discoZones` set actually changes (the engine compares before calling).
   */
  setActiveZones(zoneIds: readonly string[]): void {
    const wanted = new Set(zoneIds);
    // Turn ON any newly-listed room (build its grid the first time we see it lit).
    for (const id of wanted) {
      let z = this.zones.get(id);
      if (!z) {
        const built = this.buildZone(id);
        if (!built) continue; // unknown zone id — ignore defensively (older/edited level)
        this.zones.set(id, built);
        z = built;
      }
      z.active = true;
    }
    // Turn OFF any room no longer listed → it fades out, then update() destroys it.
    for (const [id, z] of this.zones) {
      if (!wanted.has(id)) z.active = false;
    }
  }

  /** Per-frame animation of every lit/fading room (tint/alpha only — no allocations). */
  update(dtMs: number): void {
    const dt = Math.min(dtMs, 50) / 1000;
    this.clock += dt;
    const reduced = isReducedMotion();

    for (const [id, z] of this.zones) {
      const target = z.active ? 1 : 0;
      const k = reduced || this.firstFrame ? 1 : Math.min(1, FADE_PER_SEC * dt);
      z.amount += (target - z.amount) * k;

      if (!z.active && z.amount < 0.01) {
        // Faded out — destroy the room's nodes and drop it from the map (an unlit office holds nothing).
        z.container.destroy({ children: true });
        this.zones.delete(id);
        continue;
      }

      z.container.alpha = z.amount;
      if (reduced) {
        for (const tile of z.tiles) tile.gfx.tint = STATIC_DISCO_TINT;
      } else {
        for (const tile of z.tiles) {
          const hue = (this.clock / DISCO_CYCLE_SEC + tile.phase) % 1;
          tile.gfx.tint = hsvTint(hue);
        }
      }
    }

    this.firstFrame = false;
  }

  /**
   * Build a room's disco wash: a coarse grid of large, soft, overlapping tiles CLIPPED to the zone rect
   * (so the floor lights only inside that room). Each is a white Graphics we TINT (one sprite per tile,
   * cheap) and color-cycle in update. Returns null for an unknown zone id (defensive).
   */
  private buildZone(zoneId: string): ZoneDisco | null {
    const zone = this.zoneGeometry.find((zz) => zz.id === zoneId);
    if (!zone) return null;

    const container = new Container();
    const tiles: DiscoTile[] = [];
    const cols = Math.max(2, Math.round(zone.width / TILE_SIZE));
    const rows = Math.max(2, Math.round(zone.height / TILE_SIZE));
    const cw = zone.width / cols;
    const ch = zone.height / rows;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // A soft, low-alpha cell — overlap + low alpha blends them into a smooth, moving wash (and keeps
        // the floor readable: it's a tint, not an opaque cover). Drawn at ABSOLUTE room coordinates.
        const gfx = new Graphics()
          .rect(zone.x + c * cw, zone.y + r * ch, cw + 2, ch + 2)
          .fill({ color: 0xffffff, alpha: 0.22 });
        gfx.tint = STATIC_DISCO_TINT;
        tiles.push({ gfx, phase: (r * cols + c) / (rows * cols) });
        container.addChild(gfx);
      }
    }
    container.alpha = 0; // fades in via update()
    this.root.addChild(container);
    return { container, tiles, active: false, amount: 0 };
  }

  /**
   * Release pooled references. The engine destroys the whole Pixi display tree on shutdown
   * (`app.destroy(..., {children:true})`), which tears down `root` and every child — so we only drop our
   * own references here, never double-destroy nodes.
   */
  destroy(): void {
    this.zones.clear();
  }
}

/**
 * Tint a white sprite to a fully-saturated, mid-value colour at hue `h` in [0,1) — a cheap HSV→RGB for
 * the disco cycle (saturation/value fixed). Returns a packed 0xRRGGBB int for `Graphics.tint`. No alloc
 * beyond the returned number; runs once per tile per frame, well within budget.
 */
function hsvTint(h: number): number {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const v = 1;
  const p = 0; // s = 1 → p = 0
  const q = 1 - f;
  const t = f;
  let r: number;
  let g: number;
  let b: number;
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    default: r = v; g = p; b = q; break;
  }
  return (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255);
}
